import { astShape } from '../normalize.ts';

/**
 * One parsed sort key: a column name plus its direction. The neutral,
 * engine-side normalised form shared by the transform `sort` verb
 * (mapped to arquero's `SortKey`) and `query-knowledge-graph`'s
 * `:sort` (mapped to `QkgSortKey`, which is a re-export of this type).
 */
export type SortEntry = {
  readonly column: string;
  readonly descending: boolean;
};

/**
 * Outcome of classifying a single raw-AST sort key. The two failure
 * `reason`s let each caller render its own error policy while keeping
 * the shape-discrimination here as the single source of truth:
 *
 *   - `not-sort-shape` — the entry is neither a bare column symbol nor
 *     a `(desc …)` wrapper.
 *   - `bad-desc-inner` — a `(desc …)` wrapper whose payload is not a
 *     single bare column symbol.
 *
 * `got` carries the offending `AstShape` kind so callers can name it
 * in their message.
 */
export type SortEntryResult =
  | { readonly ok: true; readonly column: string; readonly descending: boolean }
  | { readonly ok: false; readonly reason: 'not-sort-shape'; readonly got: string }
  | { readonly ok: false; readonly reason: 'bad-desc-inner'; readonly got: string };

/**
 * Single source of truth for the sort-key shape rule. A sort key is a
 * bare column symbol (ascending) or `(desc <column-symbol>)`
 * (descending) — the column always names a column of the threaded /
 * queried input. Pos-free and policy-free: callers attach their own
 * `SourcePos` and choose their own error class + output mapping.
 *
 * Consumed by `parseQkgSort` (CDF push-down, `ShapeError`),
 * `sortKeyFromArg` (arquero runtime, `TypeMismatchError`), and `sort`'s
 * `referencedColumns` hook — neither re-derives the shape rule.
 */
export const parseSortEntry = (node: unknown): SortEntryResult => {
  const shape = astShape(node);
  if (shape.kind === 'symbolRef') {
    return { ok: true, column: shape.sym, descending: false };
  }
  if (shape.kind === 'opCall' && shape.call.name === 'desc') {
    const inner = astShape((shape.call.params ?? [])[0]);
    if (inner.kind === 'symbolRef') {
      return { ok: true, column: inner.sym, descending: true };
    }
    return { ok: false, reason: 'bad-desc-inner', got: inner.kind };
  }
  return { ok: false, reason: 'not-sort-shape', got: shape.kind };
};

/**
 * SSOT for the LLM-facing sort-key grammar fragment. Embedded verbatim
 * in both the transform `sort` verb's `.describe` and
 * `query-knowledge-graph`'s `:sort` clause so the documented surface
 * cannot drift between the two tools.
 */
export const SORT_KEYS_DOC =
  'a column symbol (ascending) or `(desc <column>)` (descending), where `<column>` names a column of the input';
